import type { Component } from "solid-js";
import { createSignal, getOwner, runWithOwner, Show } from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import type { LanguageCode } from "../../../state/i18n";
import { useTranslation } from "../../../state/i18n";
import { useUserState } from "../../../state/user";
import { forward, object, partialCheck, pipe } from "valibot";
import { noop } from "lodash-es";
import UserService from "../../../services/UserService";
import InlineMessage from "../../../components/inlineMessage/InlineMessage";
import { errorTranslation, personName, textValue } from "../../../utils/validation/base";
import {
  LANGUAGE_OPTIONS,
  MAX_CHARACTERS_PASSWORD,
  MIN_CHARACTERS_PASSWORD,
  MINIMUM_ACTION_LOADING_DELAY,
} from "../../../constants";
import { createForm } from "../../../components/form/createForm";
import GenericFailureModal from "../../GenericFailureModal";
import { useModalManager } from "../../../components/modal/ModalManager";
import pMinDelay from "p-min-delay";
import SeparatorLine from "../../../components/SeparatorLine";
import { SingleSelectWithSearchInput } from "../../../components/menu/SingleSelectWithSearch";
import { SingleSelectInput } from "../../../components/menu/Select";
import { TIMEZONE_OPTIONS } from "../../../utils/timeUtil";
import { PasswordInput } from "../../../components/inputs/PasswordInput";
import SettingsPageHeader from "./components/SettingsPageHeader.tsx";
import { MoreAppError } from "../../../MoreAppError";
import { keyFromSelector } from "i18next";
import { Toggle } from "../../../components/toggle/Toggle.tsx";
import SettingSection from "./components/SettingSection.tsx";
import { EditableSettingTextValue, SettingTextValue } from "./components/SettingValue.tsx";
import SettingSectionEditActions from "./components/SettingSectionEditActions.tsx";
import SettingSectionEditButton from "./components/SettingSectionEditButton.tsx";

const UserProfilePage: Component<RouteSectionProps<never>> = () => {
  const { t } = useTranslation();

  return (
    <div class="flex h-full flex-col">
      <SettingsPageHeader
        title={t(($) => $.SETTINGS_SECTION_ACCOUNT_LABEL)}
        description={t(($) => $.SETTINGS_ITEM_PROFILE_LABEL)}
      />

      <div class="-mx-px flex h-full max-w-400 flex-col gap-6 overflow-y-auto px-px pt-8 pb-6">
        <PersonalInformationSection />
        <SeparatorLine />
        <SettingsSection />
        <SeparatorLine />
        <SecuritySection />
        <SeparatorLine />
        <CommunicationSection />
        <SeparatorLine />
      </div>
    </div>
  );
};

const PersonalInformationSection = () => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const user = useUserState();
  const [isEditingPersonalInfo, setIsEditingPersonalInfo] = createSignal(false);
  const form = createForm(
    object({
      firstName: personName(),
      lastName: personName(),
    }),
    async (value) =>
      pMinDelay(
        UserService.patchUser().mutateAsync([
          {
            op: "replace",
            path: "/settings/firstName",
            value: value.firstName,
          },
          {
            op: "replace",
            path: "/settings/lastName",
            value: value.lastName,
          },
        ]),
        MINIMUM_ACTION_LOADING_DELAY,
      )
        .then(() => setIsEditingPersonalInfo(false))
        .catch((err) =>
          open({
            content: () => <GenericFailureModal error={err} />,
          }),
        ),
    {
      firstName: user().firstName,
      lastName: user().lastName,
    },
  );

  return (
    <SettingSection
      ariaIdPrefix="pi"
      title={t(($) => $.SETTINGS_PROFILE_PI_SECTION_TITLE)}
      description={t(($) => $.SETTINGS_PROFILE_PI_SECTION_DESCRIPTION)}
    >
      <div class="flex min-w-0 grow flex-col gap-6">
        <div class="flex gap-6">
          <div class="min-w-0 flex-1">
            <EditableSettingTextValue
              label={t(($) => $.SETTINGS_PROFILE_PI_FIRST_NAME)}
              text={user().firstName ?? ""}
              editing={isEditingPersonalInfo()}
              editOnBlur={(value) => form.updateValue("firstName", value)}
              editError={form.getError("firstName")}
              editDisabled={form.isSubmitting()}
              required
            />
          </div>

          <div class="min-w-0 flex-1">
            <EditableSettingTextValue
              label={t(($) => $.SETTINGS_PROFILE_PI_LAST_NAME)}
              text={user().lastName ?? ""}
              editing={isEditingPersonalInfo()}
              editOnBlur={(value) => form.updateValue("lastName", value)}
              editError={form.getError("lastName")}
              editDisabled={form.isSubmitting()}
              required
            />
          </div>
        </div>

        <EditableSettingTextValue
          label={t(($) => $.SETTINGS_PROFILE_PI_EMAIL_ADDRESS)}
          text={user().emailAddress}
          editing={isEditingPersonalInfo()}
          editOnBlur={noop}
          editDisabled
        />

        <Show when={user().emailAddress !== user().username}>
          <EditableSettingTextValue
            label={t(($) => $.SETTINGS_PROFILE_PI_USERNAME)}
            text={user().username}
            editing={isEditingPersonalInfo()}
            editOnBlur={noop}
            editDisabled
          />
        </Show>

        <SettingSectionEditActions
          form={form}
          editing={isEditingPersonalInfo()}
          stopEditing={() => setIsEditingPersonalInfo(false)}
          cancelLabel={t(($) => $.SETTINGS_PROFILE_PI_ACTIONS_CANCEL_LABEL)}
          saveLabel={t(($) => $.SETTINGS_PROFILE_PI_ACTIONS_SAVE_LABEL)}
          savingLabel={t(($) => $.SETTINGS_PROFILE_PI_ACTIONS_SAVING_LABEL)}
        />

        <Show when={user().externallyManaged}>
          <InlineMessage variant="informative" label={t(($) => $.SETTINGS_PROFILE_PI_EXTERNALLY_MANAGED_MESSAGE)} />
        </Show>
      </div>

      <Show when={!user().externallyManaged}>
        <SettingSectionEditButton
          editing={isEditingPersonalInfo()}
          editLabel={t(($) => $.SETTINGS_PROFILE_PI_ACTIONS_EDIT_LABEL)}
          setEditing={() => setIsEditingPersonalInfo(true)}
        />
      </Show>
    </SettingSection>
  );
};

const SettingsSection = () => {
  const owner = getOwner();
  const { t, currentLanguage, changeLanguage } = useTranslation();
  const { open } = useModalManager();
  const user = useUserState();

  return (
    <SettingSection
      ariaIdPrefix="profile-settings"
      title={t(($) => $.SETTINGS_PROFILE_SETTINGS_SECTION_TITLE)}
      description={t(($) => $.SETTINGS_PROFILE_SETTINGS_SECTION_DESCRIPTION)}
      col
    >
      <SingleSelectInput<LanguageCode>
        label={t(($) => $.SETTINGS_PROFILE_SETTINGS_LANGUAGE)}
        value={currentLanguage()}
        options={LANGUAGE_OPTIONS.map(({ key, nativeLabel }) => ({
          variant: "text" as const,
          value: key,
          label: nativeLabel,
        }))}
        onSelect={(value) => {
          runWithOwner(owner, () =>
            UserService.patchUser().mutate(
              [
                {
                  op: "replace",
                  path: "/settings/language",
                  value,
                },
              ],
              {
                onSuccess: () => {
                  void changeLanguage(value);
                },
                onError: (err) => {
                  open({
                    content: () => <GenericFailureModal error={err} />,
                  });
                },
              },
            ),
          );
        }}
        sameWidth
      />

      <SingleSelectWithSearchInput
        label={t(($) => $.SETTINGS_PROFILE_SETTINGS_TIMEZONE)}
        value={user().timezone}
        options={{ timezones: TIMEZONE_OPTIONS }}
        onSelect={(value) => {
          runWithOwner(owner, () =>
            UserService.patchUser().mutate(
              [
                {
                  op: "replace",
                  path: "/settings/timeZone",
                  value,
                },
              ],
              {
                onError: (err) => {
                  open({
                    content: () => <GenericFailureModal error={err} />,
                  });
                },
              },
            ),
          );
        }}
        width="matchTrigger"
      />
    </SettingSection>
  );
};

const SecuritySection = () => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const user = useUserState();
  const [isEditingPassword, setIsEditingPassword] = createSignal(false);
  const form = createForm(
    pipe(
      object({
        currentPassword: textValue(MIN_CHARACTERS_PASSWORD, MAX_CHARACTERS_PASSWORD),
        newPassword: textValue(MIN_CHARACTERS_PASSWORD, MAX_CHARACTERS_PASSWORD),
        confirmPassword: textValue(MIN_CHARACTERS_PASSWORD, MAX_CHARACTERS_PASSWORD),
      }),
      forward(
        partialCheck(
          [["newPassword"], ["confirmPassword"]],
          (input) => input.newPassword === input.confirmPassword,
          errorTranslation(keyFromSelector(($) => $.VALIDATION_CONFIRM_PASSWORD_MISMATCH)),
        ),
        ["confirmPassword"],
      ),
    ),
    async ({ currentPassword, newPassword }) =>
      pMinDelay(
        UserService.changePassword().mutateAsync({ currentPassword, newPassword }),
        MINIMUM_ACTION_LOADING_DELAY,
      )
        .then(() => {
          setIsEditingPassword(false);
          form.reset(); // Ensure form is reset after successful password change
        })
        .catch((err) => {
          if (err instanceof MoreAppError && err.code === "invalid_password_exception") {
            form.updateError(
              "currentPassword",
              t(($) => $.VALIDATION_CURRENT_PASSWORD_INVALID),
            );
          } else {
            open({
              content: () => <GenericFailureModal error={err} />,
            });
          }
        }),
    {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  );

  return (
    <SettingSection
      ariaIdPrefix="security"
      title={t(($) => $.SETTINGS_PROFILE_SECURITY_SECTION_TITLE)}
      description={
        user().externallyManaged
          ? t(($) => $.SETTINGS_PROFILE_SECURITY_SECTION_DESCRIPTION_SSO)
          : t(($) => $.SETTINGS_PROFILE_SECURITY_SECTION_DESCRIPTION)
      }
    >
      <Show
        when={!user().externallyManaged}
        fallback={
          <div class="flex-1">
            <InlineMessage
              variant="informative"
              label={t(($) => $.SETTINGS_PROFILE_SECURITY_EXTERNALLY_MANAGED_MESSAGE)}
            />
          </div>
        }
      >
        <div class="flex-1">
          <Show
            when={isEditingPassword()}
            fallback={
              <SettingTextValue label={t(($) => $.SETTINGS_PROFILE_SECURITY_CURRENT_PASSWORD)}>
                ••••••••••••
              </SettingTextValue>
            }
          >
            <div class="flex flex-col gap-6">
              <PasswordInput
                label={t(($) => $.SETTINGS_PROFILE_SECURITY_CURRENT_PASSWORD)}
                required
                value={form.getValue("currentPassword")}
                onBlur={(e) => form.updateValue("currentPassword", e.target.value)}
                errorMessage={form.getError("currentPassword")}
                disabled={form.isSubmitting()}
              />
              <PasswordInput
                label={t(($) => $.SETTINGS_PROFILE_SECURITY_NEW_PASSWORD)}
                required
                value={form.getValue("newPassword")}
                onBlur={(e) => form.updateValue("newPassword", e.target.value)}
                errorMessage={form.getError("newPassword")}
                disabled={form.isSubmitting()}
              />
              <PasswordInput
                label={t(($) => $.SETTINGS_PROFILE_SECURITY_CONFIRM_PASSWORD)}
                required
                value={form.getValue("confirmPassword")}
                onBlur={(e) => form.updateValue("confirmPassword", e.target.value)}
                errorMessage={form.getError("confirmPassword")}
                disabled={form.isSubmitting()}
              />
              <SettingSectionEditActions
                form={form}
                editing={isEditingPassword()}
                stopEditing={() => setIsEditingPassword(false)}
                cancelLabel={t(($) => $.SETTINGS_PROFILE_SECURITY_ACTIONS_CANCEL_LABEL)}
                saveLabel={t(($) => $.SETTINGS_PROFILE_SECURITY_ACTIONS_SAVE_LABEL)}
                savingLabel={t(($) => $.SETTINGS_PROFILE_SECURITY_ACTIONS_SAVING_LABEL)}
              />
            </div>
          </Show>
        </div>

        <SettingSectionEditButton
          editing={isEditingPassword()}
          editLabel={t(($) => $.SETTINGS_PROFILE_SECURITY_ACTIONS_EDIT_LABEL)}
          setEditing={() => setIsEditingPassword(true)}
        />
      </Show>
    </SettingSection>
  );
};

const CommunicationSection = () => {
  const owner = getOwner();
  const { t } = useTranslation();
  const { open } = useModalManager();
  const user = useUserState();
  const [isMutatingNewsLetterSubscription, setIsMutatingNewsLetterSubscription] = createSignal(false);

  return (
    <SettingSection
      ariaIdPrefix="communication"
      title={t(($) => $.SETTINGS_PROFILE_COMMUNICATION_SECTION_TITLE)}
      description={t(($) => $.SETTINGS_PROFILE_COMMUNICATION_SECTION_DESCRIPTION)}
      col
    >
      <Toggle
        label={t(($) => $.SETTINGS_PROFILE_COMMUNICATION_NEWSLETTER_LABEL)}
        description={t(($) => $.SETTINGS_PROFILE_COMMUNICATION_NEWSLETTER_DESCRIPTION)}
        block
        disabled={isMutatingNewsLetterSubscription()}
        checked={user().receiveNewsLetter}
        setChecked={(value) => {
          setIsMutatingNewsLetterSubscription(true);
          runWithOwner(owner, () => {
            UserService.patchUser().mutate(
              [
                {
                  op: "replace",
                  path: "/settings/receiveNewsLetter",
                  value,
                },
              ],
              {
                onError: (err) => {
                  open({
                    content: () => <GenericFailureModal error={err} />,
                  });
                },
                onSettled: () => {
                  setIsMutatingNewsLetterSubscription(false);
                },
              },
            );
          });
        }}
      />
    </SettingSection>
  );
};

export default UserProfilePage;
